// src/components/RangeSliderControl.tsx - FIXED: Always show controls, never hide/disable
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Slider from 'rc-slider';
import type { RangeSliderFilter, ExistenceFilter } from '../types';
import 'rc-slider/assets/index.css';

interface RangeSliderControlProps {
  value: RangeSliderFilter | ExistenceFilter | null;
  onChange: (value: RangeSliderFilter | ExistenceFilter | null) => void;
  format?: 'integer' | 'float';
  min?: number;
  max?: number;
  noCommas?: boolean;
}

const RangeSliderControl: React.FC<RangeSliderControlProps> = ({
  value,
  onChange,
  format = 'integer',
  min = 0,
  max = 100,
  noCommas = false
}) => {
  const [localRange, setLocalRange] = useState<[number, number]>([min, max]);
  const [existenceState, setExistenceState] = useState<'none' | 'has_data' | 'is_empty'>('none');
  
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUpdatingFromProps = useRef(false);
  const lastAppliedValueRef = useRef<string>('');

  // Sync with external value changes
  useEffect(() => {
    isUpdatingFromProps.current = true;
    
    if (value) {
      if (value.type === 'existence') {
        setExistenceState(value.has_value ? 'has_data' : 'is_empty');
        setLocalRange([min, max]);
      } else if (value.type === 'range_slider') {
        setExistenceState('none');
        const newRange: [number, number] = [
          value.min_value ?? min,
          value.max_value ?? max
        ];
        setLocalRange(newRange);
      }
    } else {
      setExistenceState('none');
      setLocalRange([min, max]);
    }
    
    setTimeout(() => {
      isUpdatingFromProps.current = false;
    }, 0);
  }, [value, min, max]);

  const formatValue = useCallback((val: number): string => {
    if (format === 'float') {
      if (noCommas) {
        return val.toFixed(2);
      }
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(val);
    }
    if (noCommas) {
      return String(Math.round(val));
    }
    return new Intl.NumberFormat('en-US').format(Math.round(val));
  }, [format, noCommas]);

  // Debounced filter application
  const applyFilterDebounced = useCallback((newRange: [number, number]) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      if (isUpdatingFromProps.current) return;
      
      const filterValue = `${newRange[0]}-${newRange[1]}`;
      
      if (filterValue === lastAppliedValueRef.current) return;
      lastAppliedValueRef.current = filterValue;
      
      if (newRange[0] === min && newRange[1] === max) {
        onChange(null);
      } else {
        const filter: RangeSliderFilter = {
          type: 'range_slider',
          min_value: newRange[0],
          max_value: newRange[1]
        };
        onChange(filter);
      }
    }, 300);
  }, [min, max, onChange]);

  const handleSliderChange = useCallback((vals: number | number[]) => {
    if (Array.isArray(vals) && vals.length === 2) {
      const newRange: [number, number] = [vals[0], vals[1]];
      setLocalRange(newRange);
      applyFilterDebounced(newRange);
    }
  }, [applyFilterDebounced]);

  const handleInputChange = useCallback((type: 'min' | 'max', inputValue: string) => {
    const numValue = format === 'float' ? parseFloat(inputValue) : parseInt(inputValue, 10);
    if (isNaN(numValue)) return;
    
    const newRange: [number, number] = [...localRange];
    if (type === 'min') {
      newRange[0] = Math.max(min, Math.min(numValue, newRange[1]));
    } else {
      newRange[1] = Math.min(max, Math.max(numValue, newRange[0]));
    }
    
    setLocalRange(newRange);
    
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      if (isUpdatingFromProps.current) return;
      
      if (newRange[0] === min && newRange[1] === max) {
        onChange(null);
      } else {
        const filter: RangeSliderFilter = {
          type: 'range_slider',
          min_value: newRange[0],
          max_value: newRange[1]
        };
        onChange(filter);
      }
    }, 150);
  }, [localRange, min, max, format, onChange]);

  // ✅ FIXED: Existence toggle
  const handleExistenceToggle = useCallback((newState: 'has_data' | 'is_empty') => {
    const finalState = existenceState === newState ? 'none' : newState;
    setExistenceState(finalState);
    
    if (finalState !== 'none') {
      setLocalRange([min, max]);
      const existenceFilter: ExistenceFilter = {
        type: 'existence',
        has_value: finalState === 'has_data'
      };
      onChange(existenceFilter);
    } else {
      onChange(null);
    }
  }, [existenceState, min, max, onChange]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const getStep = () => {
    if (format === 'float') {
      return Math.max(0.01, (max - min) / 1000);
    }
    return Math.max(1, Math.floor((max - min) / 100));
  };

  return (
    <div className="auto-apply-slider">
      {/* REMOVE: Existence filter active indicator */}
      {/* {existenceState !== 'none' && (
        <div className="existence-active-indicator">
          <span>📊 Existence filter active: {existenceState === 'has_data' ? 'Has Data' : 'Is Empty'}</span>
          <button 
            type="button" 
            className="clear-existence" 
            onClick={() => setExistenceState('none')}
          >
            Clear
          </button>
        </div>
      )} */}

      {/* ✅ FIXED: Always show slider controls with visual state indication */}
      <div className={`slider-controls ${existenceState !== 'none' ? 'existence-mode' : ''}`}>
        <div className="slider-inputs">
          <div className="slider-input-group">
            <label className="slider-input-label">Minimum</label>
            <input
              type="text"
              value={noCommas ? String(localRange[0]) : formatValue(localRange[0])}
              onChange={(e) => {
                // Remove commas before parsing
                const raw = e.target.value.replace(/,/g, '');
                handleInputChange('min', raw);
              }}
              className="slider-number-input"
            />
          </div>
          <div className="slider-input-group">
            <label className="slider-input-label">Maximum</label>
            <input
              type="text"
              value={noCommas ? String(localRange[1]) : formatValue(localRange[1])}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, '');
                handleInputChange('max', raw);
              }}
              className="slider-number-input"
            />
          </div>
        </div>

        <div className="slider-visual-container">
          <div className="slider-range-labels">
            <span className="range-label-min">{noCommas ? String(min) : formatValue(min)}</span>
            <span className="range-label-max">{noCommas ? String(max) : formatValue(max)}</span>
          </div>
          <div className="slider-container">
            <Slider
              range
              min={min}
              max={max}
              step={getStep()}
              value={localRange}
              onChange={handleSliderChange}
              allowCross={false}
              trackStyle={[{ backgroundColor: 'var(--primary)', height: 6 }]}
              handleStyle={[
                {
                  borderColor: 'var(--primary)',
                  backgroundColor: 'white',
                  width: 20,
                  height: 20,
                  marginTop: -7,
                  boxShadow: 'var(--shadow)',
                  borderRadius: 'var(--radius-slider-handle)'
                },
                {
                  borderColor: 'var(--primary)',
                  backgroundColor: 'white',
                  width: 20,
                  height: 20,
                  marginTop: -7,
                  boxShadow: 'var(--shadow)',
                  borderRadius: 'var(--radius-slider-handle)'
                }
              ]}
              railStyle={{ backgroundColor: 'var(--gray-200)', height: 6 }}
            />
          </div>
          <div className="current-slider-range">
            <span className="current-range-display">
              {noCommas ? String(localRange[0]) : formatValue(localRange[0])} - {noCommas ? String(localRange[1]) : formatValue(localRange[1])}
            </span>
          </div>
        </div>
      </div>

      {/* Existence Toggle Buttons - Always at Bottom */}
      <div className="existence-toggle-buttons">
        <button
          type="button"
          className={`existence-toggle-btn ${existenceState === 'has_data' ? 'active' : ''}`}
          onClick={() => handleExistenceToggle('has_data')}
        >
          Has Data
        </button>
        <button
          type="button"
          className={`existence-toggle-btn ${existenceState === 'is_empty' ? 'active' : ''}`}
          onClick={() => handleExistenceToggle('is_empty')}
        >
          Is Empty
        </button>
      </div>
    </div>
  );
};

export default RangeSliderControl;